import { useState, useMemo } from 'react';
import { api } from '../../lib/api';
import { ResultViewer } from '../../components/ResultViewer';
import { useAuth } from '../../context/AuthContext';

type Specialist = 'ecommerce' | 'social';

interface EcommerceForm { productName: string; features: string; audience: string; tone: string; }
interface SocialForm { campaignObjective: string; targetAudience: string; brandVoice: string; platform: string; keyOffer: string; }

export function GeneratePage(){
  const { token, user } = useAuth();
  // especialista selecionado
  const [spec,setSpec] = useState<Specialist>('ecommerce');
  // forms estado
  const [ecommerce,setEcommerce] = useState<EcommerceForm>({ productName:'', features:'', audience:'', tone:'' });
  const [social,setSocial] = useState<SocialForm>({ campaignObjective:'', targetAudience:'', brandVoice:'', platform:'Instagram', keyOffer:'' });

  const [builtPrompt,setBuiltPrompt] = useState<string>('');
  const [result,setResult] = useState<any>(null);
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState<string| null>(null);

  function buildPrompt(): string {
    if(spec === 'ecommerce'){
      return `Você é um copywriter especialista em e-commerce.
Gere copy completa para página de produto.

PRODUTO:
Nome: ${ecommerce.productName}
Características: ${ecommerce.features}
Público-alvo: ${ecommerce.audience}
Tom: ${ecommerce.tone}

ENTREGUE:

TÍTULOS (6 opções variadas):
1. [título focado em benefício principal]
2. [título focado em dor/problema]
3. [título com urgência leve]
4. [título destacando diferencial]
5. [título com prova social implícita]
6. [título com exclusividade]

DESCRIÇÃO:
[Parágrafo 1: apresente o produto e problema que resolve]

[Parágrafo 2: destaque benefícios e diferenciais]

[Parágrafo 3: reforce valor e convide à ação]

BENEFÍCIOS:
• [benefício 1]
• [benefício 2]
• [benefício 3]
• [benefício 4]
• [benefício 5]`;
    }
    if(spec === 'social'){
      return `Você é um estrategista de social media senior.
Gere 5 ideias completas de posts para ${social.platform}.

CAMPANHA:
Objetivo: ${social.campaignObjective}
Público: ${social.targetAudience}
Tom: ${social.brandVoice}
Oferta: ${social.keyOffer || 'N/A'}

ENTREGUE 5 IDEIAS NO FORMATO:

--- IDEIA 1 ---
Hook: [frase de impacto com no máximo 80 caracteres]
Corpo: [texto do post com no máximo 240 caracteres]
CTA: [chamada para ação curta]
Hashtags: #tag1 #tag2

--- IDEIA 2 ---
[mesmo formato]

[continue até IDEIA 5]`;
    }

    return '';
  }

  async function handleGenerate(e:React.FormEvent){
    e.preventDefault(); if(!token) return;
    const finalPrompt = buildPrompt();
    setBuiltPrompt(finalPrompt);
    setLoading(true); setError(null);
    try { const r = await api.generate(finalPrompt, token); setResult(r); } catch(err:any){ setError(err.message);} finally { setLoading(false);} }

  const disableSubmit = loading ||
    (spec==='ecommerce' && !ecommerce.productName) ||
    (spec==='social' && !social.campaignObjective);

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight mb-1" style={{color:'var(--cg-heading)'}}>Gerar Conteúdo</h1>
        <p className="text-sm" style={{color:'var(--cg-text-dim)'}}>Escolha um especialista para criar conteúdo otimizado.</p>
        {user?.subscription && (
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-brand-500/10 border border-brand-500/20 text-sm">
            <i className="fa-solid fa-crown text-brand-400"></i>
            <span>Plano {user.subscription.planName}</span>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-[2fr_3fr] gap-6">
        {/* Coluna esquerda: Configuração */}
        <div className="space-y-4">
          <div className="panel p-5">
            <h2 className="text-sm font-semibold mb-3 heading">Tipo de Conteúdo</h2>
            <div className="space-y-2">
              <SpecialistOption
                active={spec==='ecommerce'}
                icon="fa-cart-shopping"
                title="E-commerce"
                desc="Títulos, descrição e bullets"
                onClick={()=>{setSpec('ecommerce'); setResult(null); setError(null);}}
              />
              <SpecialistOption
                active={spec==='social'}
                icon="fa-hashtag"
                title="Social Media"
                desc="Posts com hooks e CTAs"
                onClick={()=>{setSpec('social'); setResult(null); setError(null);}}
              />
            </div>
          </div>

          {/* Info contextual */}
          <div className="surface-soft p-4 text-xs leading-relaxed" style={{color:'var(--cg-text-dim)'}}>
            <i className="fa-solid fa-lightbulb mr-2" style={{color:'var(--cg-accent)'}}></i>
            {spec==='ecommerce' && <span>Preencha os dados do produto e receba copy otimizada para páginas de venda.</span>}
            {spec==='social' && <span>Descreva sua campanha e receba ideias prontas para postar nas redes sociais.</span>}
          </div>
        </div>

        {/* Coluna direita: Formulário */}
        <div className="panel p-6">
          <form onSubmit={handleGenerate} className="space-y-5">
            {spec==='ecommerce' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nome do Produto <span className="text-red-400">*</span></label>
                  <input
                    value={ecommerce.productName}
                    onChange={e=>setEcommerce(v=>({...v, productName:e.target.value}))}
                    required
                    className="input"
                    placeholder="Ex: Tênis UltraConfort Pro"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Características Principais</label>
                  <textarea
                    value={ecommerce.features}
                    onChange={e=>setEcommerce(v=>({...v, features:e.target.value}))}
                    rows={4}
                    className="input resize-none"
                    placeholder="Espuma de alta densidade, palmilha com memória, solado antiderrapante..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Público-Alvo</label>
                  <input
                    value={ecommerce.audience}
                    onChange={e=>setEcommerce(v=>({...v, audience:e.target.value}))}
                    className="input"
                    placeholder="Ex: Jovens ativos 20-35 anos, mães modernas, executivos..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tom de Voz</label>
                  <select
                    value={ecommerce.tone}
                    onChange={e=>setEcommerce(v=>({...v, tone:e.target.value}))}
                    className="input"
                  >
                    <option value="">Automático (IA decide)</option>
                    <option value="profissional">Profissional</option>
                    <option value="moderno">Moderno e Dinâmico</option>
                    <option value="luxuoso">Luxuoso/Premium</option>
                    <option value="amigavel">Amigável/Acessível</option>
                    <option value="urgente">Urgente/Persuasivo</option>
                    <option value="tecnico">Técnico/Detalhado</option>
                  </select>
                </div>
              </div>
            )}

            {spec==='social' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Objetivo da Campanha <span className="text-red-400">*</span></label>
                  <textarea
                    value={social.campaignObjective}
                    onChange={e=>setSocial(v=>({...v, campaignObjective:e.target.value}))}
                    required
                    rows={3}
                    className="input resize-none"
                    placeholder="Ex: Lançar nova coleção eco-friendly com 30% de desconto"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Público-alvo</label>
                  <input
                    value={social.targetAudience}
                    onChange={e=>setSocial(v=>({...v, targetAudience:e.target.value}))}
                    className="input"
                    placeholder="Consumidores conscientes 25-40 anos"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tom de Voz</label>
                  <select
                    value={social.brandVoice}
                    onChange={e=>setSocial(v=>({...v, brandVoice:e.target.value}))}
                    className="input"
                  >
                    <option value="">Automático (IA decide)</option>
                    <option value="profissional">Profissional</option>
                    <option value="casual">Casual/Descontraído</option>
                    <option value="divertido">Divertido/Engraçado</option>
                    <option value="inspirador">Inspirador/Motivacional</option>
                    <option value="educativo">Educativo/Informativo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Plataforma</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { id: 'Instagram', icon: 'fa-brands fa-instagram' },
                      { id: 'Facebook', icon: 'fa-brands fa-facebook' },
                      { id: 'TikTok', icon: 'fa-brands fa-tiktok' },
                      { id: 'LinkedIn', icon: 'fa-brands fa-linkedin' },
                      { id: 'Twitter/X', icon: 'fa-brands fa-x-twitter' },
                      { id: 'YouTube', icon: 'fa-brands fa-youtube' },
                      { id: 'Pinterest', icon: 'fa-brands fa-pinterest' },
                      { id: 'Threads', icon: 'fa-brands fa-threads' },
                    ].map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSocial(v=>({...v, platform:p.id}))}
                        className={`p-2 rounded-lg border-2 transition-all text-center ${
                          social.platform === p.id
                            ? 'border-brand-500 bg-brand-500/10'
                            : 'border-[var(--cg-border)] hover:border-[var(--cg-border-strong)] bg-[var(--cg-surface)]'
                        }`}
                      >
                        <div className="text-lg"><i className={p.icon}></i></div>
                        <div className="text-[9px] font-medium mt-0.5">{p.id.split('/')[0]}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Oferta ou Destaque Especial</label>
                  <input
                    value={social.keyOffer}
                    onChange={e=>setSocial(v=>({...v, keyOffer:e.target.value}))}
                    className="input"
                    placeholder="Frete grátis, 20% OFF, brinde exclusivo..."
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="panel-alt p-3 flex items-center gap-2 text-sm" style={{background:'var(--cg-danger-bg)', borderColor:'var(--cg-danger)'}}>
                <i className="fa-solid fa-triangle-exclamation" style={{color:'var(--cg-danger)'}}></i>
                <span style={{color:'var(--cg-danger)'}}>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={disableSubmit}
              className="btn btn-primary w-full h-12 text-base"
            >
              {loading ? (
                <><i className="fa-solid fa-spinner fa-spin"></i> Gerando conteúdo...</>
              ) : (
                <><i className="fa-solid fa-wand-magic-sparkles"></i> Gerar Conteúdo</>
              )}
            </button>
          </form>
        </div>

        {/* Resultado */}
        {(result || loading) && (
          <div className="mt-8 fade-in lg:col-span-2">
            <h2 className="text-lg font-semibold heading mb-4">Resultado</h2>

            {loading && (
              <div className="space-y-3" aria-busy="true">
                <div className="panel p-6 animate-pulse">
                  <div className="h-4 bg-slate-700/50 rounded w-1/3 mb-3"></div>
                  <div className="h-3 bg-slate-700/30 rounded w-full mb-2"></div>
                  <div className="h-3 bg-slate-700/30 rounded w-5/6"></div>
                </div>
                <div className="panel p-6 animate-pulse">
                  <div className="h-3 bg-slate-700/30 rounded w-2/3"></div>
                </div>
              </div>
            )}

            {!loading && <ResultViewer raw={result} specialist={spec} />}
          </div>
        )}
      </div>
    </div>
  );
}

// Componente auxiliar para opções de especialista
function SpecialistOption({active, icon, title, desc, onClick}: {active:boolean; icon:string; title:string; desc:string; onClick:()=>void}){
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left p-3 rounded-lg border transition-all duration-200"
      style={{
        background: active ? 'var(--cg-accent-soft)' : 'var(--cg-surface)',
        borderColor: active ? 'var(--cg-accent)' : 'var(--cg-border)',
        borderWidth: active ? '2px' : '1px'
      }}
    >
      <div className="flex items-start gap-3">
        <i className={`fa-solid ${icon} text-lg mt-0.5 transition-colors duration-200`} style={{color: active ? 'var(--cg-accent)' : 'var(--cg-text-dim)'}}></i>
        <div className="flex-1">
          <div className="text-sm font-semibold mb-0.5 transition-colors duration-200" style={{color: active ? 'var(--cg-accent)' : 'var(--cg-text)'}}>{title}</div>
          <div className="text-xs" style={{color:'var(--cg-text-dim)'}}>{desc}</div>
        </div>
        <i className={`fa-solid fa-circle-check transition-all duration-200 ${active ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`} style={{color:'var(--cg-accent)'}}></i>
      </div>
    </button>
  );
}
